/**
 * CharacterPane — R232q22 Phase 3 (Sirus 1:1 UI rebuild).
 *
 * WoW 3.3.5a / Sirus character pane. Toggled with `C` while the world stage
 * is active; `Esc` closes it. Replaces the legacy CharacterPanel.tsx for the
 * new Sirus chrome — that file is kept around for the wowhead 3D iframe
 * preview which we deliberately defer here.
 *
 * Layout (1100×600 panel, centred):
 *   ┌──────── Left half — paperdoll ────────┬──────── Right half — stats ────────┐
 *   │  [Голова]                  [Шея]      │  [ Характеристики ] [ Сопрот. ]    │
 *   │  [Плечи ]   ┌──────────┐   [Спина]    │  [ Навыки ] [ PvP ] [ Репутация ]  │
 *   │  [Грудь ]   │ Portrait │   [Рубаха]   │                                    │
 *   │  [Запяст]   │  64×64   │   [Табард]   │  Сила              45              │
 *   │  [Перчат]   └──────────┘   [Кольцо1]  │  Ловкость          30              │
 *   │  [Талия ]                  [Кольцо2]  │  …                                 │
 *   │  [Ноги  ]                  [Тринкет1] │                                    │
 *   │  [Стопы ]                  [Тринкет2] │                                    │
 *   │      [Главная] [Доп.] [Дальняя]       │                                    │
 *   └────────────────────────────────────────┴────────────────────────────────────┘
 *
 * Slot semantics:
 *   – Click on filled slot → select (gold border, internal selection state).
 *   – Right-click on filled slot → unequip (move back to bag via equip-store).
 *   – Shift+click on filled slot → emit `aiwari-chat-link` window event so
 *     Chat.tsx (or any future link router) can drop the item id into the
 *     chat box. Pure dispatch; this component does not import realmClient.
 *   – Hover with item → schedule UnitTooltip-style item tooltip; deferred to
 *     a later task (the UnitTooltip component is unit-only). For now we use
 *     the native `title` attribute as a stop-gap so users still see name +
 *     ilvl.
 *
 * Data sources:
 *   – `useEquip` for equipment slots map (Slot → itemId).
 *   – `useNet().players[selfId]` for level / class / hp / power.
 *   – `useUI().draft` for race / name fallback when the live row hasn't
 *     replicated yet.
 *   – `useUI().stage` to gate the keyboard listener to the world stage so we
 *     don't fight character-creator's own `C` shortcut.
 *   – `computeEquipmentBonuses` from `state/effective-stats.ts` for the
 *     stat panel. The full `effectiveStats(player, equipped)` helper hinted
 *     at in the brief does not exist yet — we render base + bonuses and
 *     '-' for everything the server hasn't replicated.
 *
 * Position: fixed centre of the viewport, z-index 40, with a black/60 modal
 * backdrop that blocks click-through to the world.
 *
 * Mobile: `@media (pointer: coarse)` flips to a full-screen sheet with
 * paperdoll above stats (single column), gold trim shrunk via SirusFrame.
 *
 * Russian text only. Tokens via `var(--aiwari-...)`. Cinzel + JetBrains Mono
 * + EB Garamond fonts only — no Tailwind.
 */

import { useEffect, useMemo, useState } from 'react';
import { useEquip } from '../../state/equip-store.js';
import { useNet } from '../../state/net-store.js';
import { useUI } from '../../state/store.js';
import { useModalStackRegister } from '../../state/active-modals-store.js';
import {
  ITEMS,
  RARITY_COLOUR,
  iconFor,
  type ItemDef,
  type Slot,
} from '../../data/equipment.js';
import { CLASSES } from '../../data/classes.js';
import { RACES, type RaceId } from '../../data/wow-races.js';
import { computeEquipmentBonuses } from '../../state/effective-stats.js';

// ─────────────────────────────────────────────────────────────────────────
// Slot taxonomy + glyph fallbacks. Order is the WoW 3.3.5a paperdoll order
// so the visual columns line up with WotLK 3.3.5a muscle memory.
// ─────────────────────────────────────────────────────────────────────────

const LEFT_COLUMN_SLOTS: Slot[] = [
  'head', 'shoulders', 'chest', 'wrists', 'hands', 'waist', 'legs', 'feet',
];

const RIGHT_COLUMN_SLOTS: Slot[] = [
  'neck', 'cape', 'shirt', 'tabard', 'ring1', 'ring2', 'trinket1', 'trinket2',
];

const WEAPON_SLOTS: Slot[] = ['mainhand', 'offhand', 'ranged'];

const SLOT_LABEL: Record<Slot, string> = {
  head: 'Голова',
  neck: 'Шея',
  shoulders: 'Плечи',
  shirt: 'Рубаха',
  tabard: 'Табард',
  chest: 'Грудь',
  wrists: 'Запястья',
  hands: 'Перчатки',
  waist: 'Талия',
  legs: 'Ноги',
  feet: 'Стопы',
  cape: 'Спина',
  ring1: 'Кольцо 1',
  ring2: 'Кольцо 2',
  trinket1: 'Тринкет 1',
  trinket2: 'Тринкет 2',
  mainhand: 'Главная рука',
  offhand: 'Доп. рука',
  ranged: 'Дальняя',
};

/** Greyscale silhouette glyph used when a slot is empty. */
const SLOT_GLYPH: Record<Slot, string> = {
  head: '👑',
  neck: '📿',
  shoulders: '🪶',
  shirt: '👕',
  tabard: '🎽',
  chest: '🥋',
  wrists: '🦾',
  hands: '🧤',
  waist: '🪢',
  legs: '👖',
  feet: '👢',
  cape: '🧣',
  ring1: '💍',
  ring2: '💍',
  trinket1: '🔮',
  trinket2: '🔮',
  mainhand: '⚔️',
  offhand: '🛡️',
  ranged: '🏹',
};

// ─────────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────────

type TabId = 'stats' | 'resist' | 'skills' | 'pvp' | 'rep';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'stats', label: 'Характеристики' },
  { id: 'resist', label: 'Сопротивления' },
  { id: 'skills', label: 'Навыки' },
  { id: 'pvp', label: 'Честь / PvP' },
  { id: 'rep', label: 'Репутация' },
];

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function getRaceTint(raceId: RaceId | string | null | undefined): string {
  if (!raceId) return '#5C4724';
  const found = RACES.find((r) => r.id === raceId);
  return found?.colour ?? '#5C4724';
}

function fmt(n: number | null | undefined, suffix = ''): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '-';
  return `${Math.round(n)}${suffix}`;
}

function fmtPct(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '-';
  return `${n.toFixed(1)}%`;
}

// ─────────────────────────────────────────────────────────────────────────
// Slot box — single 36×36 paperdoll cell. Reuses the global
// `.aiwari-icon-slot` class for hover + sizing; quality border is layered
// on top via inline style (border-color override) so the rarity is visible.
// ─────────────────────────────────────────────────────────────────────────

interface SlotBoxProps {
  slot: Slot;
  itemId: string | undefined;
  selected: boolean;
  onSelect: (slot: Slot) => void;
  onUnequip: (slot: Slot) => void;
  onChatLink: (item: ItemDef) => void;
}

function SlotBox({
  slot,
  itemId,
  selected,
  onSelect,
  onUnequip,
  onChatLink,
}: SlotBoxProps) {
  const item = itemId ? ITEMS.find((i) => i.id === itemId) : undefined;
  const rarityColour = item ? RARITY_COLOUR[item.rarity] : 'var(--aiwari-frame-edge)';
  const borderColour = selected ? 'var(--aiwari-gold)' : rarityColour;
  const glyph = SLOT_GLYPH[slot];
  const icon = item ? iconFor(item) : null;
  const isImg = typeof icon === 'string' && icon.startsWith('http');

  const tooltip = item
    ? `${item.name.ru}\nУровень предмета ${item.ilvl}\nЛКМ — выбрать · ПКМ — снять · Shift+ЛКМ — в чат`
    : SLOT_LABEL[slot];

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!item) return;
    if (e.shiftKey) {
      onChatLink(item);
      return;
    }
    onSelect(slot);
  };

  const handleContext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (item) onUnequip(slot);
  };

  return (
    <button
      type="button"
      className="aiwari-icon-slot"
      title={tooltip}
      aria-label={item ? `${SLOT_LABEL[slot]}: ${item.name.ru}` : SLOT_LABEL[slot]}
      onClick={handleClick}
      onContextMenu={handleContext}
      style={{
        borderColor: borderColour,
        borderWidth: selected ? 2 : 1,
        boxShadow: item ? `inset 0 0 6px ${rarityColour}55` : 'none',
        background: item ? 'rgba(0,0,0,0.45)' : 'var(--aiwari-frame-stone)',
      }}
    >
      {item && isImg ? (
        <img src={icon!} alt="" />
      ) : (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--aiwari-text-display)',
            lineHeight: 1,
            filter: item ? undefined : 'opacity(0.35) grayscale(1)',
          }}
        >
          {item ? icon : glyph}
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Stat row — aligned label + value pair used by every tab. Pure layout.
// ─────────────────────────────────────────────────────────────────────────

interface StatRowProps {
  label: string;
  value: string;
  bonus?: number;
  accent?: string;
}

function StatRow({ label, value, bonus, accent }: StatRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        padding: '4px 8px',
        borderBottom: '1px dashed hsl(44 30% 25% / 0.4)',
        gap: 8,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--aiwari-font-body)',
          fontSize: 'var(--aiwari-text-md)',
          color: accent ?? 'var(--aiwari-fg-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--aiwari-font-mono)',
          fontSize: 'var(--aiwari-text-md)',
          color: 'var(--aiwari-fg)',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
        {typeof bonus === 'number' && bonus > 0 && (
          <span style={{ color: 'var(--aiwari-quality-uncommon)', marginLeft: 6 }}>
            +{bonus}
          </span>
        )}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Section header — small Cinzel caps banner separating stat groups.
// ─────────────────────────────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'var(--aiwari-font-caps)',
        fontSize: 'var(--aiwari-text-sm)',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--aiwari-gold-soft)',
        margin: '12px 8px 4px',
        borderBottom: '1px solid var(--aiwari-frame-edge)',
        paddingBottom: 2,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CharacterPane
// ─────────────────────────────────────────────────────────────────────────

export function CharacterPane() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TabId>('stats');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const stage = useUI((s) => s.stage);
  const draft = useUI((s) => s.draft);
  const selfPlayer = useNet((s) => (s.selfId ? s.players[s.selfId] : undefined));
  const equipped = useEquip((s) => s.equipped);
  const unequipLocal = useEquip((s) => s.unequip);

  // Keyboard: C toggles, Esc closes — but only when world stage is active so
  // we don't conflict with character-creator shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (useUI.getState().stage !== 'world') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'c' || e.key === 'C' || e.key === 'с' || e.key === 'С') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('aiwari-open-character', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('aiwari-open-character', onOpenEvent);
    };
  }, [open]);

  // Register on the global modal stack so Esc-from-anywhere closes us last.
  useModalStackRegister('character-pane', open, () => setOpen(false));

  // Pre-compute equipment bonuses; cheap (≤ 19 items) but memo'd for stable
  // object identity across re-renders.
  const bonuses = useMemo(() => computeEquipmentBonuses(equipped), [equipped]);

  // Derived character header values.
  const classId = (selfPlayer?.classId ?? draft.classId ?? null) as string | null;
  const cls = classId ? CLASSES.find((c) => c.id === classId) : undefined;
  const raceId = draft.raceId ?? null;
  const race = raceId ? RACES.find((r) => r.id === raceId) : undefined;
  const tint = getRaceTint(raceId);
  const name = selfPlayer?.name ?? draft.name ?? '-';
  const level = selfPlayer?.level ?? null;

  if (!open || stage !== 'world') return null;

  const handleSelect = (slot: Slot) => {
    setSelectedSlot((cur) => (cur === slot ? null : slot));
  };

  const handleUnequip = (slot: Slot) => {
    unequipLocal(slot);
    if (selectedSlot === slot) setSelectedSlot(null);
    // Server sync intentionally deferred — legacy CharacterPanel.tsx still
    // owns the /inventory/unequip POST; the new Sirus chrome here is a
    // visual rebuild only. Once Phase-3 wave 2 lands, route through the
    // realm client directly.
  };

  const handleChatLink = (item: ItemDef) => {
    window.dispatchEvent(
      new CustomEvent('aiwari-chat-link', { detail: { itemId: item.id, name: item.name.ru } }),
    );
  };

  return (
    <>
      {/* Backdrop — clicking outside closes. */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 39,
        }}
      />
      <div
        data-aiwari-character-pane
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 40,
          width: 1100,
          maxWidth: 'calc(100vw - 32px)',
          height: 600,
          maxHeight: 'calc(100vh - 32px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/*
         * Mobile: collapse to single-column sheet. Desktop tokens
         * leak through SirusFrame so we override at the wrapper level.
         */}
        <style>{`
          @media (pointer: coarse) {
            [data-aiwari-character-pane] {
              top: 0 !important;
              left: 0 !important;
              transform: none !important;
              width: 100vw !important;
              height: 100vh !important;
              max-width: none !important;
              max-height: none !important;
            }
            [data-aiwari-character-pane] [data-pane-body] {
              flex-direction: column !important;
            }
            [data-aiwari-character-pane] [data-pane-half] {
              width: 100% !important;
              max-width: none !important;
            }
          }
        `}</style>
        <div
          className="aiwari-modal aiwari-filigree-frame"
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            minHeight: 0,
          }}
        >
          <span className="aiwari-filigree-bl" />
          <span className="aiwari-filigree-br" />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
            }}
          >
            {/* Header row — name / race / class / level + close button. */}
            <div
              className="aiwari-modal-title"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                padding: '8px 16px',
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: 'var(--aiwari-font-display)',
                    fontSize: 'var(--aiwari-text-display)',
                    fontWeight: 600,
                    color: cls?.colour ?? 'var(--aiwari-gold)',
                    lineHeight: 1.1,
                    letterSpacing: 0.6,
                  }}
                >
                  {name}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--aiwari-font-body)',
                    fontSize: 'var(--aiwari-text-md)',
                    color: 'var(--aiwari-fg-muted)',
                    marginTop: 2,
                    letterSpacing: 0,
                  }}
                >
                  Уровень {level ?? '-'} · {race?.name.ru ?? '-'} · {cls?.name.ru ?? '-'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Закрыть"
                style={{
                  width: 28,
                  height: 28,
                  background: 'hsl(0 0% 0% / 0.55)',
                  border: '1px solid var(--aiwari-frame-edge)',
                  borderRadius: 'var(--aiwari-frame-radius)',
                  color: 'var(--aiwari-fg-muted)',
                  fontSize: 16,
                  cursor: 'pointer',
                  fontFamily: 'var(--aiwari-font-mono)',
                }}
              >
                ×
              </button>
            </div>

            {/* Body — paperdoll left, stats right. */}
            <div
              data-pane-body
              style={{
                display: 'flex',
                gap: 12,
                flex: '1 1 auto',
                minHeight: 0,
                padding: 14,
              }}
            >
              {/* ───── LEFT HALF: paperdoll ──────────────────────────── */}
              <div
                data-pane-half
                style={{
                  flex: '1 1 50%',
                  maxWidth: 540,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                    flex: '1 1 auto',
                    minHeight: 0,
                  }}
                >
                  {/* Left column of slots */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {LEFT_COLUMN_SLOTS.map((slot) => (
                      <SlotBox
                        key={slot}
                        slot={slot}
                        itemId={equipped[slot]}
                        selected={selectedSlot === slot}
                        onSelect={handleSelect}
                        onUnequip={handleUnequip}
                        onChatLink={handleChatLink}
                      />
                    ))}
                  </div>

                  {/* Centre — character portrait / 3D miniature placeholder. */}
                  <div
                    style={{
                      flex: '1 1 auto',
                      alignSelf: 'stretch',
                      minWidth: 160,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    <div
                      aria-label="Портрет персонажа"
                      style={{
                        width: 200,
                        height: 280,
                        background: `
                          radial-gradient(ellipse at 50% 38%,
                            hsl(0 0% 100% / 0.10) 0%,
                            transparent 60%),
                          linear-gradient(160deg, ${tint} 0%, var(--aiwari-frame-stone) 60%, hsl(0 0% 0% / 0.85) 100%)`,
                        border: '2px solid var(--aiwari-gold)',
                        borderRadius: 'var(--aiwari-frame-radius)',
                        boxShadow:
                          'inset 0 0 0 1px hsl(44 100% 49% / 0.4), inset 0 0 22px hsl(0 0% 0% / 0.7), 0 0 22px hsl(44 100% 49% / 0.25)',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        paddingBottom: 8,
                        fontFamily: 'var(--aiwari-font-caps)',
                        fontSize: 12,
                        letterSpacing: '0.15em',
                        color: 'var(--aiwari-fg)',
                        textShadow: '0 1px 0 #000',
                      }}
                    >
                      {cls?.name.ru ?? 'Портрет'}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--aiwari-font-body)',
                        fontSize: 11,
                        fontStyle: 'italic',
                        color: 'var(--aiwari-fg-dim)',
                        textAlign: 'center',
                      }}
                    >
                      3D миниатюра — позже
                    </div>
                  </div>

                  {/* Right column of slots */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {RIGHT_COLUMN_SLOTS.map((slot) => (
                      <SlotBox
                        key={slot}
                        slot={slot}
                        itemId={equipped[slot]}
                        selected={selectedSlot === slot}
                        onSelect={handleSelect}
                        onUnequip={handleUnequip}
                        onChatLink={handleChatLink}
                      />
                    ))}
                  </div>
                </div>

                {/* Bottom centre — weapon row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 10,
                    marginTop: 12,
                    paddingTop: 8,
                    borderTop: '1px solid var(--aiwari-frame-edge)',
                  }}
                >
                  {WEAPON_SLOTS.map((slot) => (
                    <SlotBox
                      key={slot}
                      slot={slot}
                      itemId={equipped[slot]}
                      selected={selectedSlot === slot}
                      onSelect={handleSelect}
                      onUnequip={handleUnequip}
                      onChatLink={handleChatLink}
                    />
                  ))}
                </div>
              </div>

              {/* ───── RIGHT HALF: stats panel ───────────────────────── */}
              <div
                data-pane-half
                style={{
                  flex: '1 1 50%',
                  maxWidth: 540,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  borderLeft: '1px solid var(--aiwari-frame-edge)',
                  paddingLeft: 12,
                }}
              >
                {/* Tab strip */}
                <div
                  role="tablist"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                    paddingBottom: 6,
                    borderBottom: '1px solid var(--aiwari-frame-edge)',
                  }}
                >
                  {TABS.map((t) => {
                    const active = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setTab(t.id)}
                        className="aiwari-stone-btn"
                        style={{
                          padding: '4px 10px',
                          background: active
                            ? 'linear-gradient(180deg, var(--aiwari-gold) 0%, var(--aiwari-gold-dark) 100%)'
                            : undefined,
                          color: active ? '#0F0900' : 'var(--aiwari-fg-muted)',
                          borderColor: active ? 'var(--aiwari-gold-dark)' : undefined,
                          borderRadius: 'var(--aiwari-frame-radius)',
                          fontFamily: 'var(--aiwari-font-caps)',
                          fontSize: 10,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          fontWeight: active ? 700 : 500,
                          textShadow: active ? '0 1px 0 hsl(48 100% 80% / 0.5)' : undefined,
                          boxShadow: active
                            ? 'inset 0 1px 0 hsl(48 100% 90% / 0.5), 0 1px 2px hsl(0 0% 0% / 0.5)'
                            : undefined,
                        }}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab body — independently scrollable */}
                <div
                  role="tabpanel"
                  style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    paddingRight: 6,
                  }}
                >
                  {tab === 'stats' && (
                    <>
                      <SectionHeader>Основные</SectionHeader>
                      <StatRow
                        label="Сила"
                        value={fmt((selfPlayer?.pow ?? 0) + bonuses.pow)}
                        bonus={bonuses.pow}
                      />
                      <StatRow label="Ловкость" value="-" />
                      <StatRow label="Стойкость" value="-" />
                      <StatRow label="Интеллект" value="-" />
                      <StatRow label="Дух" value="-" />

                      <SectionHeader>Защита</SectionHeader>
                      <StatRow
                        label="Броня"
                        value={fmt((selfPlayer?.armor ?? 0) + bonuses.armor)}
                        bonus={bonuses.armor}
                      />
                      <StatRow label="Оборона" value="-" />
                      <StatRow label="Парирование" value="-" />
                      <StatRow label="Уклонение" value="-" />
                      <StatRow label="Блок" value="-" />

                      <SectionHeader>Атака</SectionHeader>
                      <StatRow
                        label="Шанс крита"
                        value={fmtPct(selfPlayer?.critPct)}
                        bonus={bonuses.crit}
                      />
                      <StatRow
                        label="Ускорение"
                        value={fmtPct(selfPlayer?.hastePct)}
                        bonus={bonuses.haste}
                      />
                      <StatRow
                        label="Мастерство"
                        value={fmtPct(selfPlayer?.masteryPct)}
                        bonus={bonuses.mastery}
                      />

                      <SectionHeader>Регенерация</SectionHeader>
                      <StatRow label="Регенерация маны" value="-" />
                      <StatRow label="Регенерация ХП" value="-" />
                    </>
                  )}

                  {tab === 'resist' && (
                    <>
                      <SectionHeader>Сопротивления магии</SectionHeader>
                      <StatRow label="Тайн" value="-" />
                      <StatRow label="Огонь" value="-" />
                      <StatRow label="Лёд" value="-" />
                      <StatRow label="Природа" value="-" />
                      <StatRow label="Тень" value="-" />
                      <StatRow label="Свет" value="-" />
                    </>
                  )}

                  {tab === 'skills' && (
                    <>
                      <SectionHeader>Оружейные навыки</SectionHeader>
                      <StatRow label="Мечи (одноруч.)" value="-" />
                      <StatRow label="Мечи (двуруч.)" value="-" />
                      <StatRow label="Топоры (одноруч.)" value="-" />
                      <StatRow label="Топоры (двуруч.)" value="-" />
                      <StatRow label="Дробящее (одноруч.)" value="-" />
                      <StatRow label="Дробящее (двуруч.)" value="-" />
                      <StatRow label="Кинжалы" value="-" />
                      <StatRow label="Посохи" value="-" />
                      <StatRow label="Луки" value="-" />
                      <StatRow label="Арбалеты" value="-" />
                      <StatRow label="Огнестрел" value="-" />
                      <StatRow label="Метание" value="-" />
                    </>
                  )}

                  {tab === 'pvp' && (
                    <>
                      <SectionHeader>Честь</SectionHeader>
                      <StatRow
                        label="Очки чести"
                        value={fmt(selfPlayer?.realmPoints ?? 0)}
                      />
                      <StatRow
                        label="Убийств за честь"
                        value={fmt(selfPlayer?.realmKills ?? 0)}
                      />
                      <StatRow
                        label="Смертей"
                        value={fmt(selfPlayer?.realmDeaths ?? 0)}
                      />
                      <SectionHeader>Поля сражений</SectionHeader>
                      <StatRow label="Сегодня побед" value="-" />
                      <StatRow label="Всего побед" value="-" />
                      <StatRow label="Жетоны арены" value="-" />
                    </>
                  )}

                  {tab === 'rep' && (
                    <>
                      <SectionHeader>Репутация</SectionHeader>
                      <div
                        style={{
                          padding: '8px',
                          fontFamily: 'var(--aiwari-font-body)',
                          fontSize: 12,
                          fontStyle: 'italic',
                          color: 'var(--aiwari-fg-dim)',
                        }}
                      >
                        Список фракций появится после интеграции системы
                        репутации.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
